import React, { useState, useEffect } from 'react';
import { fetchOpenAIText } from '../../utils/openaiClient';
import './ProsConsExercise.css';

const ProsConsExercise = ({
  node, updateAttributes, editor: _editor, getPos: _getPos, onClose,
  // Board mode: direct props when node is not available
  topic: topicProp, cefr: cefrProp, pros: prosProp, cons: consProp,
  isStudent = false,
  myState = {}, otherState = {}, updateState = () => {},
}) => {
  const data = node ? {
    topic: node.attrs.topic,
    cefr: node.attrs.cefr,
    pros: node.attrs.pros,
    cons: node.attrs.cons,
    hideInstructions: node.attrs.hideInstructions,
  } : {
    topic: topicProp || '',
    cefr: cefrProp || 'B1',
    pros: prosProp || [],
    cons: consProp || [],
    hideInstructions: false,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [editableTopic, setEditableTopic] = useState(data.topic);
  const [editableCefr, setEditableCefr] = useState(data.cefr);
  const [editablePros, setEditablePros] = useState([...data.pros]);
  const [editableCons, setEditableCons] = useState([...data.cons]);
  const [editableHideInstructions, setEditableHideInstructions] = useState(data.hideInstructions);
  const [discussionQuestions, setDiscussionQuestions] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState('');
  const [questionsShared, setQuestionsShared] = useState(false);

  // Mount-restore: recover questions state when exercise is opened mid-session
  useEffect(() => {
    if (isStudent) {
      if (Array.isArray(otherState.questions) && otherState.questions.length > 0 && otherState.questionsVisible) {
        setDiscussionQuestions(otherState.questions);
        setQuestionsShared(true);
      }
    } else {
      if (Array.isArray(myState.questions) && myState.questions.length > 0) {
        setDiscussionQuestions(myState.questions);
        setQuestionsShared(!!myState.questionsVisible);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time: student sees/hides questions when teacher shares or hides
  useEffect(() => {
    if (!isStudent) return;
    if (!otherState.questionsVisible) {
      setQuestionsShared(false);
      return;
    }
    if (Array.isArray(otherState.questions) && otherState.questions.length > 0) {
      setDiscussionQuestions(otherState.questions);
      setQuestionsShared(true);
    }
  }, [otherState.questionsVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = () => setIsEditing(true);
  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        topic: editableTopic,
        cefr: editableCefr,
        pros: editablePros,
        cons: editableCons,
        hideInstructions: editableHideInstructions,
      });
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditableTopic(data.topic);
    setEditableCefr(data.cefr);
    setEditablePros([...data.pros]);
    setEditableCons([...data.cons]);
    setEditableHideInstructions(data.hideInstructions);
  };

  const updateList = (type, index, value) => {
    if (type === 'pros') {
      setEditablePros(prev => { const u = [...prev]; u[index] = value; return u; });
    } else {
      setEditableCons(prev => { const u = [...prev]; u[index] = value; return u; });
    }
  };

  const normalize = (s) => String(s || '')
    .toLowerCase()
    .replace(/^[\s\-•\d.)]+/, '')
    .replace(/[^a-z0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const cleanGenerated = (text) => {
    if (!text) return '';
    let t = String(text).trim();
    t = t.replace(/^[\s\-•\d.)]+/, '');
    const firstLine = t.split(/\n|;|•|•|-|.|–/).filter(Boolean)[0] || t;
    return firstLine.trim().slice(0, 120);
  };

  const generateItem = async (kind) => {
    const topic = (editableTopic || '').trim();
    const level = (editableCefr || 'B1').trim();
    const existingRaw = (kind === 'pros' ? editablePros : editableCons)
      .filter(Boolean)
      .filter(v => !/magic is happening/i.test(v));
    const existingNorm = existingRaw.map(normalize).filter(Boolean);
    const dir = kind === 'pros' ? 'a concise PRO' : 'a concise CON';
    const levelGuidance = (() => {
      const L = level.toUpperCase();
      if (L === 'A1') return 'Use very basic, high-frequency words; simple concrete ideas.';
      if (L === 'A2') return 'Use basic everyday vocabulary; short simple phrases.';
      if (L === 'B1') return 'Use straightforward vocabulary; simple linking (because, so).';
      if (L === 'B2') return 'Use more precise vocabulary; add a brief rationale word if needed.';
      if (L === 'C1') return 'Use nuanced vocabulary; compact but more specific wording.';
      if (L === 'C2') return 'Use sophisticated, precise vocabulary; keep it succinct.';
      return 'Use straightforward vocabulary; simple phrasing.';
    })();
    const prompt = [
      `You generate one short argument as plain text (no list formatting).`,
      `Topic: ${topic || 'General topic'}`,
      `CEFR Level: ${level} — ${levelGuidance}`,
      `Existing ${kind}: ${existingRaw.join(' | ') || '—'}`,
      `Task: Generate ${dir} for the topic. Max 8 words.`,
      `Must not repeat or paraphrase existing items.`,
      `No numbering, no bullets, no leading symbols.`,
      `No punctuation at the start; avoid quotes; keep neutral tone.`,
      `Output: ONLY the phrase.`
    ].join('\n');
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        const out = await fetchOpenAIText({ prompt, temperature: 0.6, maxTokens: 48 });
        const cleaned = cleanGenerated(out);
        const norm = normalize(cleaned);
        if (norm && !existingNorm.includes(norm)) return cleaned;
      }
      return '';
    } catch (e) {
      return '';
    }
  };

  const addItem = async (type) => {
    if (type === 'pros') {
      const index = editablePros.length;
      setEditablePros(prev => [...prev, 'magic is happening...']);
      const gen = await generateItem('pros');
      setEditablePros(prev => { const next = [...prev]; next[index] = gen || ''; return next; });
    } else {
      const index = editableCons.length;
      setEditableCons(prev => [...prev, 'magic is happening...']);
      const gen = await generateItem('cons');
      setEditableCons(prev => { const next = [...prev]; next[index] = gen || ''; return next; });
    }
  };

  const removeItem = (type, index) => {
    if (type === 'pros') setEditablePros(prev => prev.filter((_, i) => i !== index));
    else setEditableCons(prev => prev.filter((_, i) => i !== index));
  };

  const buildQuestionsPrompt = (topicText, lvl) => [
    `Generate 5 open-ended discussion questions about the topic below.`,
    `CEFR Level: ${lvl}. Keep each question concise (<=14 words).`,
    `Return ONLY JSON: { "questions": ["Q1?", "Q2?", "Q3?", "Q4?", "Q5?"] }`,
    '',
    `Topic: ${topicText}`
  ].join('\n');

  const handleGenerateQuestions = async () => {
    try {
      setGenError('');
      setGenLoading(true);
      const topicText = (editableTopic || '').trim() || 'General topic';
      const lvl = (editableCefr || 'B1').trim();
      const prompt = buildQuestionsPrompt(topicText, lvl);
      let out = await fetchOpenAIText({ prompt, temperature: 0.4, maxTokens: 220 });
      out = (out || '').trim();
      if (out.startsWith('```')) {
        const m = out.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
        if (m && m[1]) out = m[1].trim();
      }
      let parsed = null;
      try { parsed = JSON.parse(out); } catch (_) {
        const m2 = out.match(/\{[\s\S]*\}$/);
        if (m2) { try { parsed = JSON.parse(m2[0]); } catch (_) {} }
      }
      const qs = Array.isArray(parsed?.questions)
        ? parsed.questions.filter(q => typeof q === 'string' && q.trim()).slice(0, 5)
        : [];
      if (!qs.length) throw new Error('Invalid questions JSON');
      setDiscussionQuestions(qs);
      // Save to own state (not shared yet)
      updateState({ questions: qs, questionsVisible: false });
    } catch (e) {
      setGenError(e?.message || 'Failed to generate');
    } finally {
      setGenLoading(false);
    }
  };

  const handleShareQuestions = () => {
    setQuestionsShared(true);
    updateState({ questions: discussionQuestions, questionsVisible: true });
  };

  const handleHideQuestions = () => {
    setQuestionsShared(false);
    updateState({ questionsVisible: false });
  };

  return (
    <div className="pc-exercise">
      <div className="pc-header">
        <div className="pc-header-left">
          <div className="pc-header-icon">
            <svg width="20" height="20" viewBox="0 0 682.66669 682.66669" fill="white">
              <g transform="matrix(1.3333333,0,0,-1.3333333,0,682.66667)">
                <g>
                  <g clipPath="url(#clipPath3632)">
                    <g transform="translate(327,255)">
                      <path d="m 0,0 h 140 c 16.568,0 30,-13.432 30,-30 v -180 c 0,-16.568 -13.432,-30 -30,-30 H 0 c -16.568,0 -30,13.432 -30,30 v 180 c 0,16.568 13.432,30 30,30 z" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(45,255)">
                      <path d="m 0,0 h 140 c 16.568,0 30,-13.432 30,-30 v -180 c 0,-16.568 -13.432,-30 -30,-30 H 0 c -16.568,0 -30,13.432 -30,30 v 180 c 0,16.568 13.432,30 30,30 z" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(115,497)">
                      <path d="m 0,0 v 0 c 49.706,0 90,-40.294 90,-90 0,-49.706 -40.294,-90 -90,-90 -49.706,0 -90,40.294 -90,90 0,49.706 40.294,90 90,90 z" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(397,497)">
                      <path d="m 0,0 v 0 c 49.706,0 90,-40.294 90,-90 0,-49.706 -40.294,-90 -90,-90 -49.706,0 -90,40.294 -90,90 0,49.706 40.294,90 90,90 z" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(75,75)">
                      <path d="M 0,0 H 80" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(75,135)">
                      <path d="M 0,0 H 80" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(75,195)">
                      <path d="M 0,0 H 80" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(437,75)">
                      <path d="M 0,0 H -80" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(437,135)">
                      <path d="M 0,0 H -80" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(437,195)">
                      <path d="M 0,0 H -80" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(115,255)">
                      <path d="M 0,0 V 62" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(397,255)">
                      <path d="M 0,0 V 62" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(146.8198,428.2129)">
                      <path d="M 0,0 -42.426,-42.426 -63.64,-21.213" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(418.2134,428.2129)">
                      <path d="M 0,0 -42.427,-42.426" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                    <g transform="translate(375.7866,428.2129)">
                      <path d="M 0,0 42.427,-42.426" fill="none" stroke="white" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTopic}
              onChange={(e) => setEditableTopic(e.target.value)}
              className="pc-title-input"
              placeholder="Topic"
            />
          ) : (
            <span className="pc-title">{editableTopic || 'Pros and Cons'}</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableCefr}
              onChange={(e) => setEditableCefr(e.target.value)}
              className="pc-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="pc-level-badge">{editableCefr}</span>
          )}
        </div>
        <div className="pc-header-actions">
          {!isEditing && !isStudent && (
            <button className="pc-icon-btn" aria-label="Edit" data-tip="Edit" onClick={handleEdit}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {!isEditing && onClose && (
            <button className="pc-icon-btn" aria-label="Close" data-tip="Close" onClick={onClose}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="pc-body">
        {/* How to use — hidden from students always */}
        {!editableHideInstructions && !isStudent && (
          <div className="pc-collapsible">
            <div
              className="pc-collapsible-header"
              onClick={() => setShowInstructions(!showInstructions)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowInstructions(!showInstructions)}
            >
              <span className="pc-collapsible-title">How to use this exercise</span>
              <svg
                className={`pc-collapsible-arrow${showInstructions ? ' is-open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </div>
            {showInstructions && (
              <div className="pc-collapsible-content">
                <ul className="pc-instructions-list">
                  <li>Brainstorm pros and cons in pairs or small groups.</li>
                  <li>Keep items short; focus on clarity over detail.</li>
                  <li>Prioritize and vote for the strongest points on each side.</li>
                  <li>Discuss briefly, prompting for examples/evidence where relevant.</li>
                  <li>Conclude with a quick summary or personal stance.</li>
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="pc-columns">
          <div className="pc-col">
            <div className="pc-col-header pros">
              <span className="pc-col-header-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              Pros
            </div>
            {editablePros.map((pro, i) => (
              <div className="pc-item" key={i}>
                {isEditing ? (
                  <input
                    value={pro}
                    onChange={(e) => updateList('pros', i, e.target.value)}
                    className={`pc-item-input${/magic is happening/i.test(pro) ? ' magic-loading' : ''}`}
                    placeholder="Enter pro..."
                    readOnly={/magic is happening/i.test(pro)}
                  />
                ) : (
                  <span className="pc-item-text pros">
                    <span className="pc-item-icon">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                    <span>{pro}</span>
                  </span>
                )}
                {isEditing && (
                  <button className="pc-remove-btn" onClick={() => removeItem('pros', i)} title="Remove">×</button>
                )}
              </div>
            ))}
            {isEditing && (
              <button className="pc-add-btn" onClick={() => addItem('pros')}>+ Add Pro</button>
            )}
          </div>
          <div className="pc-col">
            <div className="pc-col-header cons">
              <span className="pc-col-header-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </span>
              Cons
            </div>
            {editableCons.map((con, i) => (
              <div className="pc-item" key={i}>
                {isEditing ? (
                  <input
                    value={con}
                    onChange={(e) => updateList('cons', i, e.target.value)}
                    className={`pc-item-input${/magic is happening/i.test(con) ? ' magic-loading' : ''}`}
                    placeholder="Enter con..."
                    readOnly={/magic is happening/i.test(con)}
                  />
                ) : (
                  <span className="pc-item-text cons">
                    <span className="pc-item-icon">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                    <span>{con}</span>
                  </span>
                )}
                {isEditing && (
                  <button className="pc-remove-btn" onClick={() => removeItem('cons', i)} title="Remove">×</button>
                )}
              </div>
            ))}
            {isEditing && (
              <button className="pc-add-btn" onClick={() => addItem('cons')}>+ Add Con</button>
            )}
          </div>
        </div>

        {/* Teacher-only: generate + share discussion questions */}
        {!isEditing && !isStudent && (
          <div className="pc-questions-card">
            <div className="pc-questions-toolbar">
              <button
                type="button"
                onClick={handleGenerateQuestions}
                disabled={genLoading}
                className="pc-questions-btn"
              >
                {genLoading ? 'Generating…' : discussionQuestions.length > 0 ? 'Regenerate questions' : 'Add discussion questions'}
              </button>
              {discussionQuestions.length > 0 && (
                <button
                  type="button"
                  className={`pc-share-toggle${questionsShared ? ' is-shared' : ''}`}
                  onClick={questionsShared ? handleHideQuestions : handleShareQuestions}
                >
                  <span className="pc-share-dot" />
                  {questionsShared ? 'Visible to student' : 'Show to student'}
                </button>
              )}
            </div>
            {genError && <div className="pc-questions-error">{genError}</div>}
            {discussionQuestions.length > 0 && (
              <div className="pc-questions-body">
                <ol className="pc-questions-list">
                  {discussionQuestions.map((q, i) => (
                    <li key={i}>
                      <span className="pc-q-num">{i + 1}</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Student-only: show questions when teacher has shared them */}
        {isStudent && questionsShared && discussionQuestions.length > 0 && (
          <div className="pc-questions-card" style={{ marginTop: 20 }}>
            <div className="pc-questions-toolbar">
              <span className="pc-collapsible-title">Discussion Questions</span>
            </div>
            <div className="pc-questions-body">
              <ol className="pc-questions-list">
                {discussionQuestions.map((q, i) => (
                  <li key={i}>
                    <span className="pc-q-num">{i + 1}</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="pc-footer">
          <button className="pc-cancel-btn" onClick={handleCancel}>Cancel</button>
          <button className="pc-save-btn" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
};

export default ProsConsExercise;
